import request, { generateNotyMessage } from 'utils/request'
import { actions as notyActions } from 'layouts/ErrorBox'
import { logSentryEvent } from 'utils/sentry'
import { INSTRUMENT_TYPE_MAP } from 'utils/constants'
import { enrichTaskMetadataForSave } from './taskInstrumentMetadata'
import { withoutHiddenClinroParticipantFields } from './clinroParticipantFields'

export const INSTRUMENT_V2_BASE = studyID => `/control/v2/studies/${studyID}/instruments`

export const instrumentV2Urls = {
  list: (studyID, query = '') => `${INSTRUMENT_V2_BASE(studyID)}${query || ''}`,
  create: studyID => INSTRUMENT_V2_BASE(studyID),
  one: (studyID, instrumentID, query = '') =>
    `${INSTRUMENT_V2_BASE(studyID)}/${instrumentID}${query || ''}`,
  deploy: (studyID, instrumentID, query = '') =>
    `${INSTRUMENT_V2_BASE(studyID)}/${instrumentID}/deploy${query || ''}`,
  filters: (studyID, instrumentID) => `${INSTRUMENT_V2_BASE(studyID)}/${instrumentID}/filters`,
  schedule: (studyID, instrumentID, scheduleId, query = '') =>
    `${INSTRUMENT_V2_BASE(studyID)}/${instrumentID}/schedules/${scheduleId}${query || ''}`,
  job: (studyID, jobID) => `/control/v2/studies/${studyID}/jobs/${jobID}`,
}

/**
 * Body shape expected by PUT /v2/studies/{id}/instruments/{id}
 */
export const buildV2InstrumentUpdateBody = instrument => {
  const source = withoutHiddenClinroParticipantFields(instrument)
  const isTask =
    source?.type === INSTRUMENT_TYPE_MAP.task || !!source?.metadata?.task_name
  let metadata = source.metadata
  if (isTask && metadata) {
    metadata = enrichTaskMetadataForSave(metadata, {
      id: instrument.id,
      title: instrument.title,
      task_name: metadata.task_name || instrument.task_name,
    })
  }

  const body = {
    title: source.title,
    metadata,
  }
  if (source.study_version_id != null) {
    body.study_version_id = source.study_version_id
  }
  if (source.instrument_roles != null) {
    body.instrument_roles = source.instrument_roles
  }
  if (source.map_progress_weight != null) {
    body.map_progress_weight = source.map_progress_weight
  }
  if (source.is_geofencing_enabled != null) {
    body.is_geofencing_enabled = source.is_geofencing_enabled
  }
  return body
}

/**
 * Schedule saves may normalize visibility, defaults, and other metadata helpers.
 * Re-merge only schedule fields onto the last persisted metadata snapshot so the
 * backend does not treat unrelated metadata diffs as a new instrument version.
 */
export const applyPersistedMetadataForScheduleSave = (preparedInstrument, persistedInstrument) => {
  if (!preparedInstrument?.metadata) return preparedInstrument
  if (!persistedInstrument?.metadata) return preparedInstrument

  const baselineMetadata = persistedInstrument.metadata
  const pinnedVersion = baselineMetadata.version != null ? baselineMetadata.version : 1

  let metadata = {
    ...baselineMetadata,
    schedule: preparedInstrument.metadata.schedule,
    instrument_reuse: preparedInstrument.metadata.instrument_reuse,
    version: pinnedVersion,
  }

  // Trigger-another-item IDs live on metadata, not on the schedule object.
  // Keep the values from this save so schedule version pinning cannot drop them.
  if (Object.prototype.hasOwnProperty.call(preparedInstrument.metadata, 'conditions')) {
    metadata.conditions = preparedInstrument.metadata.conditions
  }
  if (Object.prototype.hasOwnProperty.call(preparedInstrument.metadata, 'conditions_data')) {
    metadata.conditions_data = preparedInstrument.metadata.conditions_data
  }

  // Visit item type and confirmation are edited on the Schedule page but stored on
  // instrument metadata. Keep this save's values, including false, and remove keys
  // the editor cleared so the persisted snapshot cannot restore a stale visit setting.
  const visitMetadataKeys = ['article_type', 'deploy_on_confirm']
  visitMetadataKeys.forEach(key => {
    if (Object.prototype.hasOwnProperty.call(preparedInstrument.metadata, key)) {
      metadata[key] = preparedInstrument.metadata[key]
    } else {
      delete metadata[key]
    }
  })

  if (preparedInstrument?.type === INSTRUMENT_TYPE_MAP.task) {
    metadata = enrichTaskMetadataForSave(metadata, {
      id: preparedInstrument.id,
      title: preparedInstrument.title,
      task_name: metadata.task_name,
    })
  }

  return {
    ...preparedInstrument,
    map_progress_weight: persistedInstrument.map_progress_weight ?? preparedInstrument.map_progress_weight,
    metadata,
  }
}

const TERMINAL_JOB_STATUSES = new Set(['completed', 'failed'])
const DEFAULT_POLL_INTERVAL_MS = 2500
const DEFAULT_POLL_TIMEOUT_MS = 5 * 60 * 1000

const delay = ms => new Promise(resolve => setTimeout(resolve, ms))

/**
 * Client-side X-Trace-Id for V2 instrument async ops (UUID when available).
 */
export const createInstrumentTraceId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, char => {
    const random = (Math.random() * 16) | 0
    const value = char === 'x' ? random : (random & 0x3) | 0x8
    return value.toString(16)
  })
}

export const instrumentV2TraceHeaders = (traceId = createInstrumentTraceId()) => ({
  'X-Trace-Id': traceId,
})

const logInstrumentV2Trace = ({ message, traceId, level = 'error', extra = {} }) => {
  if (!traceId) return
  // Prefer response/job trace_id for support correlation (Splunk / Sentry).
  console.error(`[instrument V2] ${message}, trace_id=${traceId}`, extra)
  logSentryEvent({
    message: `[instrument V2] ${message}`,
    level,
    tags: { trace_id: traceId },
    contexts: { instrument_v2: { trace_id: traceId, ...extra } },
  })
}

/**
 * DELETE /v2/studies/{studyID}/instruments/{instrumentID} — whole-instrument soft-delete (async 202).
 * Not delete-one-schedule.
 */
export const deleteInstrumentV2 = ({ studyID, instrumentID, studyVersionId, traceId } = {}) => {
  const resolvedTraceId = traceId || createInstrumentTraceId()
  const query = studyVersionId != null && studyVersionId !== '' ? `?study_version_id=${studyVersionId}` : ''

  return request({
    method: 'DELETE',
    url: instrumentV2Urls.one(studyID, instrumentID, query),
    // No body — omit Content-Type so browsers don't omit Content-Length while advertising JSON
    contentType: null,
    hasLoader: false,
    skipNotySuccess: true,
    headerOptions: instrumentV2TraceHeaders(resolvedTraceId),
    success: json => ({
      ...json,
      // Prefer server trace_id; keep client id as fallback for poll + support logs
      trace_id: json?.trace_id || resolvedTraceId,
      _clientTraceId: resolvedTraceId,
    }),
    fail: (_response, content) => {
      const failedTraceId = content?.trace_id || resolvedTraceId
      logInstrumentV2Trace({
        message: 'delete instrument request failed',
        traceId: failedTraceId,
        extra: { status: _response?.status, error: content?.error || content?.message },
      })
      return Promise.reject(
        new Error(content?.message || content?.error || 'Failed to delete instrument'),
      )
    },
  })
}

/**
 * GET /v2/studies/{studyID}/jobs/{jobID} — single status fetch (used by pollInstrumentJob).
 */
export const getInstrumentJobStatusV2 = ({ studyID, jobID, traceId } = {}) =>
  request({
    url: instrumentV2Urls.job(studyID, jobID),
    hasLoader: false,
    skipNotySuccess: true,
    headerOptions: traceId ? instrumentV2TraceHeaders(traceId) : undefined,
    success: json => json,
    fail: (_response, content) => {
      const failedTraceId = content?.trace_id || traceId
      logInstrumentV2Trace({
        message: 'job status request failed',
        traceId: failedTraceId,
        extra: { job_id: jobID, status: _response?.status },
      })
      return Promise.reject(
        new Error(content?.message || content?.error || 'Failed to fetch instrument job status'),
      )
    },
  })

/**
 * Poll GET /v2/studies/{studyID}/jobs/{jobID} until completed or failed.
 * Returns the final job payload. Rejects on failed / timeout.
 */
export const pollInstrumentJob = ({
  studyID,
  jobID,
  traceId,
  intervalMs = DEFAULT_POLL_INTERVAL_MS,
  timeoutMs = DEFAULT_POLL_TIMEOUT_MS,
}) => {
  return dispatch => {
    const startedAt = Date.now()

    const pollOnce = () =>
      dispatch(getInstrumentJobStatusV2({ studyID, jobID, traceId })).then(json => {
        const status = json?.status
        const jobTraceId = json?.trace_id || traceId
        if (TERMINAL_JOB_STATUSES.has(status)) {
          if (status === 'failed') {
            const errMsg = json?.error || 'Instrument job failed'
            logInstrumentV2Trace({
              message: 'instrument job failed',
              traceId: jobTraceId,
              extra: { job_id: jobID, error: errMsg },
            })
            return Promise.reject(new Error(errMsg))
          }
          return json
        }
        if (Date.now() - startedAt > timeoutMs) {
          logInstrumentV2Trace({
            message: 'instrument job poll timed out',
            traceId: jobTraceId,
            extra: { job_id: jobID },
          })
          return Promise.reject(new Error('Timed out waiting for instrument job to complete'))
        }
        return delay(intervalMs).then(pollOnce)
      })

    return pollOnce()
  }
}

/**
 * Run an async V2 operation that returns 202 + job_id, then poll until done.
 * `startRequest` must be a thunk that resolves to the 202 JSON body.
 */
export const runInstrumentV2AsyncJob = ({
  studyID,
  startRequest,
  successMessage,
  failMessage = 'Instrument operation failed',
  traceId,
}) => {
  return dispatch =>
    dispatch(startRequest)
      .then(json => {
        const jobID = json?.job_id
        const resolvedTraceId = json?.trace_id || json?._clientTraceId || traceId
        if (!jobID) {
          // Sync-style success (no job) — treat as done
          return json
        }
        return dispatch(pollInstrumentJob({ studyID, jobID, traceId: resolvedTraceId }))
      })
      .then(result => {
        if (successMessage) {
          dispatch(notyActions.showSuccess({ text: generateNotyMessage(successMessage, true) }))
        }
        return result
      })
      .catch(err => {
        dispatch(
          notyActions.showError({
            text: generateNotyMessage(err?.message || failMessage, false),
          }),
        )
        return Promise.reject(err)
      })
}
